/**
 * Which market-chart image section 2 of the proposal should show — or whether it should show
 * one at all.
 *
 * ASX Energy replaced its old site (asxenergy.com.au). The Australian-average chart that
 * section 2 used to carry no longer exists; the new site publishes some states and not
 * others. Management's decision was NSW/VIC/QLD/SA in, WA/TAS out — but that is ASX's
 * editorial choice and it has already changed once, so it is not written down here.
 *
 * WHICH STATES HAVE A CHART IS DATA, NOT CODE: a state with an uploaded image gets one, a
 * state without gets none. WA and TAS show no chart because nobody has uploaded one, and if
 * ASX starts publishing TAS tomorrow that is an upload, not a release. There is no fallback
 * image either — the old Australian average is gone, and printing a stale chart would be
 * worse than printing nothing.
 *
 * Section 2 renders ONCE per tender — it sits before the per-site block that pages 2+ clone —
 * so the chart is chosen at tender level from the states of the tender's sites. On prod that
 * is unambiguous almost always: 553 of 651 tenders have a single site, and only 9 have sites
 * in more than one state.
 *
 * Shared between the server (proposal generation) and the admin screen.
 */

/**
 * The states the admin screen offers before anyone configures anything — the four ASX Energy
 * publishes today.
 *
 * A STARTING LIST, NOT A RESTRICTION. Nothing checks it at generation time and the API
 * accepts any state code, so an admin who needs one it does not cover just adds it. It exists
 * only so the screen opens on the states that are actually worth setting, instead of every
 * state the org happens to own a site in.
 */
export const DEFAULT_CHART_STATES = ['NSW', 'VIC', 'QLD', 'SA'] as const;

/**
 * States that fall back to another state's chart when they have none of their own.
 *
 * ACT sits inside the NEM's NSW1 region — the same wholesale market, the same price curve —
 * so NSW's chart is the accurate one for its 115 sites, and leaving them chartless would drop
 * the chart from more tenders than WA and TAS combined. This is a fact about the electricity
 * market rather than a choice about content, which is why it is here and the state list is
 * not. An ACT image uploaded directly always wins over this.
 */
export const STATE_CHART_FALLBACK: Record<string, string> = { ACT: 'NSW' };

export type StateImage = {
  state_code: string;
  image_url: string;
};

export type ResolvedStateMarketImage = {
  /** The URL to substitute, or null when the proposal must show no chart. */
  url: string | null;
  /** The state whose chart is used — may differ from the site's under a fallback. */
  state: string | null;
  /** True when section 2 should render without a chart. */
  excluded: boolean;
  /** Why no chart applies — logged, and shown to whoever generated the proposal. */
  reason: string | null;
};

/** Normalise a state value: trim, upper-case, treat empty as absent. */
export function normaliseState(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toUpperCase();
  return s.length > 0 ? s : null;
}

/** A state's own image, or the one it falls back to. Null when neither exists. */
function findImage(state: string, stateImages: readonly StateImage[]): StateImage | null {
  const own = stateImages.find(i => normaliseState(i.state_code) === state && i.image_url);
  if (own) return own;

  const fallbackState = STATE_CHART_FALLBACK[state];
  if (!fallbackState) return null;
  return stateImages.find(i => normaliseState(i.state_code) === fallbackState && i.image_url) ?? null;
}

/**
 * @param siteStates - `addr_state` of every site on the tender, in any order, blanks included
 * @param stateImages - the org's uploaded charts
 */
export function resolveStateMarketImage(
  siteStates: readonly (string | null | undefined)[],
  stateImages: readonly StateImage[],
): ResolvedStateMarketImage {
  const states = [...new Set(
    siteStates.map(normaliseState).filter((s): s is string => s !== null),
  )].sort();

  if (states.length === 0) {
    return {
      url: null, state: null, excluded: true,
      reason: 'No site on this tender has a state recorded, so no market chart is shown.',
    };
  }

  if (states.length > 1) {
    // Charts are per state; a tender spanning several has no single correct one, and picking
    // one arbitrarily would misrepresent the others.
    return {
      url: null, state: null, excluded: true,
      reason: `This tender covers ${states.join(', ')} — no single state chart applies, so none is shown.`,
    };
  }

  const siteState = states[0];
  const match = findImage(siteState, stateImages);

  if (!match) {
    return {
      url: null, state: null, excluded: true,
      reason: `No market chart is set for ${siteState}, so none is shown. Upload one in Admin → State Images if there should be.`,
    };
  }

  return {
    url: match.image_url,
    state: normaliseState(match.state_code),
    excluded: false,
    reason: null,
  };
}

/**
 * Sentinel written into the image's `src` when no chart applies.
 *
 * Placeholder substitution can only replace text, never delete an element, so exclusion
 * happens in two steps: the token resolves to this value, and the block cleanup pass that
 * already strips empty rows and pages removes the chart. An empty `src` would render as a
 * broken-image icon instead.
 */
export const NO_STATE_CHART = '__no_state_chart__';

/**
 * Comment markers the template wraps the chart column in, so removal takes the whole column
 * rather than just the picture.
 *
 * Section 2 is a two-column flex layout: `.chart-section` at 65% holding a grey `.chart-box`
 * and a "Source: asxenergy.com.au" line, and `.text-section` at 35% holding the explanation
 * of the Australian market. Deleting only the <img> would leave an empty grey box two-thirds
 * of the page wide, still credited to a source that is no longer shown.
 *
 * Comment markers rather than matching `<div class="chart-section">` because that div nests
 * others, and regex cannot count closing tags.
 */
export const STATE_CHART_MARKER_START = '<!--state-chart-->';
export const STATE_CHART_MARKER_END = '<!--/state-chart-->';

/**
 * Remove the market chart from a block of HTML, keeping the explanation beside it.
 *
 * The brief was "omit the chart but keep the explanation about the Australian market", so the
 * text column stays and widens to fill the space the chart column occupied — an inline style,
 * which beats the 35% on the class without the template needing another rule.
 *
 * No-ops when the sentinel is absent, so a proposal that HAS a chart is untouched. Falls back
 * to removing the bare <img> on templates that predate the markers, which at least avoids a
 * broken image.
 */
export function stripStateChartImage(html: string): string {
  if (typeof html !== 'string' || !html.includes(NO_STATE_CHART)) return html;

  // Literal rather than built from the constants above: a template literal silently drops
  // the backslash in \s, turning the character class into a plain [sS]. Kept in step with
  // STATE_CHART_MARKER_START / _END, which the template must use verbatim.
  const markers = /<!--state-chart-->[\s\S]*?<!--\/state-chart-->/g;

  let out = html;
  if (out.includes(STATE_CHART_MARKER_START) && out.includes(STATE_CHART_MARKER_END)) {
    out = out.replace(markers, '');
    // Let the explanation use the full width now that nothing sits beside it.
    out = out.replace(
      /class="text-section"/g,
      'class="text-section" style="width:100%"',
    );
  }

  // Whether or not the markers were there, no <img> may keep the sentinel as its src.
  return out.replace(
    // Backslashes doubled: this IS a template literal, so `\b` would collapse to `b`.
    new RegExp(`<img\\b[^>]*\\bsrc\\s*=\\s*["']?[^"'>]*${NO_STATE_CHART}[^"'>]*["']?[^>]*>`, 'gi'),
    '',
  );
}
